import type {ValueEditorValidations} from '../../../kp-value-editor/types';
import type {Validator} from 'svelte-forms';
import {isDefined, isFunction, isNull, isNullOrUndefined, isUndefined} from '../../../../utils/custom-utils';
import {requiredValidator} from '../../shared/validators';
import type {Formatter, Modifier, Parser} from '../../forms/types';
import type {MultipleAcceptableValueEditorOptions} from './types';
import type {ActionReturn, Action} from 'svelte/action';

export function validatorFactory(validations: ValueEditorValidations): Validator[] {
    const validators: Validator[] = [];

    if (isDefined(validations.required) && validations.required) {
        validators.push(requiredValidator());
    }

    return validators;
}


export function getOptionIdResolver<VALUE, ID>(options: MultipleAcceptableValueEditorOptions<VALUE, ID>): (_: VALUE) => ID {
    return (option) => {
        try {
            return options.optionIdResolver({option});
        } catch (e) {
            throw new Error('Error in custom optionIdResolver', {cause: e});
        }
    }
}

export function getComparator<VALUE>(options: MultipleAcceptableValueEditorOptions<VALUE>): (element1: VALUE, element2: VALUE) => number | undefined {
    return (element1, element2) => {
        try {
            return options.sortComparator({element1, element2});
        } catch (e) {
            throw new Error('Error in custom sortComparator', {cause: e});
        }
    };
}

export function getOptionDisabledResolver<VALUE>(options: MultipleAcceptableValueEditorOptions<VALUE>, model) {
    if (!isFunction(options.optionDisabledResolver)) {
        throw new Error(`optionDisabledResolver is not function: ${options.optionDisabledResolver}`);
    }
    return (option: VALUE) => {
        try {
            return options.optionDisabledResolver({item: option, options, model});
        } catch (e) {
            throw new Error('Error in custom optionDisabledResolver', {cause: e});
        }
    }
}

export function tryToFindOptionById<VALUE, ID>(options: VALUE[], idResolver: (_: VALUE) => ID, id: ID): VALUE {
    return options.find((option) => idResolver(option) === id);
}

export function getOptionById<VALUE, ID>(options: VALUE[], idResolver: (_: VALUE) => ID, id: ID): VALUE {
    const foundOption = tryToFindOptionById(options, idResolver, id);

    if (isUndefined(foundOption)) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        throw new Error(`Acceptable option with ID: ${id} was not found`);
    }
    return foundOption;
}

export function getOptionId<VALUE, ID>(option: VALUE,  idResolver: (_: VALUE) => ID): ID {
    if (isNull(option)) {
        return null;
    }
    return idResolver(option);
}

export function modelToIdentifierFormatter<T = any>(options: MultipleAcceptableValueEditorOptions<T>): Formatter {
    return (values: T[]) => {
        if (values === null) {
            return null;
        }

        return values.map((value) => getOptionId(value, getOptionIdResolver(options)));
    }
}

// acceptable values can change over time (e.g. search facets update after new search)
export function identifierToModelParser<T = any>(acceptableValuesProvider: () => T[], options: MultipleAcceptableValueEditorOptions<T>): Parser {
    return (values: string[]) => {
        if (values === null) {
            return null;
        }
        return values.map((value) => getOptionById(acceptableValuesProvider(), getOptionIdResolver(options), value));
    }
}

export function sortModelParser<T = any>(options: MultipleAcceptableValueEditorOptions<T>): Modifier {
    return (values: T[]) => {
        if (values === null) {
            return null;
        }
        if (options.reorderable || !isFunction(options.sortComparator)) {
            return values;
        }
        return values.toSorted(getComparator(options))
    }
}

export type ActionDestroyHandle = () => void;
export type ApplyAction<ELEMENT extends HTMLElement> = (element: ELEMENT) => ActionDestroyHandle

export function useAction<ELEMENT extends HTMLElement, PARAM>(action: Action<ELEMENT, PARAM>, parameter?: PARAM): ApplyAction<ELEMENT> {
    return (element) => {
        const actionResult: ActionReturn<PARAM> = action.call(null, element, parameter);
        return actionResult && isFunction(actionResult.destroy) ? actionResult.destroy : () => {/* noop */};
    }
}

export function useActionsOnElement<ELEMENT extends HTMLElement>(element: ELEMENT, applyActions: ApplyAction<ELEMENT>[], handlers: ActionDestroyHandle[] | null) {
    if (isNullOrUndefined(handlers)) {
        handlers = applyActions.map((applyAction) => applyAction(element));
    } else {
        detachActions(handlers);
        handlers = applyActions.map((applyAction) => applyAction(element));
    }
    return handlers;
}

export function detachActions(handlers: ActionDestroyHandle[] | null): ActionDestroyHandle[] | null {
    if (isNullOrUndefined(handlers)) {
        return handlers;
    }
    handlers.forEach((handler) => handler());
    return null;
}